import {forwardRef, useRef, useState} from "react";
import {Eye, EyeOff} from "@chayka/icons";
import {Input} from "../Input";

type PasswordInputProps = React.ComponentPropsWithoutRef<"input">;

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({className, ...props}, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    const assignRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof ref === "function") {
        ref(node);
        return;
      }

      if (ref) {
        ref.current = node;
      }
    };

    const toggleVisibility = () => {
      const input = inputRef.current;

      if (!input) {
        setIsVisible((prev) => !prev);
        return;
      }

      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? input.value.length;

      setIsVisible((prev) => !prev);

      requestAnimationFrame(() => {
        const nextInput = inputRef.current;

        if (!nextInput) {
          return;
        }

        nextInput.focus();
        nextInput.setSelectionRange(start, end);
      });
    };

    return (
      <Input
        {...props}
        ref={assignRef}
        type={isVisible ? "text" : "password"}
        className={[
          "w-full px-4 py-2 border rounded border-gray-300 focus:outline-none focus:border-blackout",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        endAdornment={
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={toggleVisibility}
            className="ml-2 text-secondary-text focus:outline-none"
            aria-label={isVisible ? "Hide password" : "Show password"}
          >
            {isVisible ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        }
      />
    );
  },
);

PasswordInput.displayName = "PasswordInput";
